import svg from '../js/svg';

const SECTION_ORDER = [
  'ADs_PersonalDetails',
  'ADs_MaritalStatus',
  'ADs_ChildrenDetails',
  'ADs_Devise',
  'ADs_Heirs',
  'ADs_Trust',
  'ADs_Guardian',
  'ADs_Equity',
];

const val = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

const joinAddress = (parts) => {
  const text = parts.filter(Boolean).join(', ');
  return text || '—';
};

const personName = (person) => {
  if (!person) return '—';
  return (
    person.full_name ||
    [person.f_name, person.s_name].filter(Boolean).join(' ') ||
    person.name ||
    '—'
  );
};

const childName = (child) =>
  child?.full_name ||
  [child?.f_name, child?.s_name].filter(Boolean).join(' ') ||
  child?.children ||
  '—';

const rowsHtml = (rows) => {
  if (!rows.length) {
    return `<p class="text-sm text-gray-500 mb-0">No details recorded.</p>`;
  }
  return `
    <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mb-0">
      ${rows
        .map(
          ([label, value]) => `
        <div class="min-w-0">
          <dt class="text-xs uppercase tracking-wide text-gray-500">${label}</dt>
          <dd class="text-sm text-gray-800 break-words whitespace-pre-line mb-0">${val(value)}</dd>
        </div>`
        )
        .join('')}
    </dl>
  `;
};

const listBlock = (title, items) => {
  if (!items?.length) return '';
  return `
    <div class="mb-3">
      <p class="text-sm font-medium text-gray-700 mb-1">${title}</p>
      <ul class="list-disc pl-5 text-sm text-gray-800 space-y-1 mb-0">
        ${items.map((item) => `<li>${item}</li>`).join('')}
      </ul>
    </div>
  `;
};

const formatPersonal = (detail) => {
  const parties = ['party1', 'party2', 'party3', 'party4', 'party5']
    .map((key) => detail?.[key])
    .filter(Boolean);

  if (!parties.length) return rowsHtml([]);

  return parties
    .map((party, index) => {
      const rows = [
        ['Name', personName(party)],
        ['Email', party.email_address],
        ['Cell', party.cell_nr],
        ['ID number', party.id_nr],
        ['Passport', party.passport_nr],
        [
          'Physical address',
          joinAddress([
            party.physical_address,
            party.physical_suburb,
            party.physical_town,
            party.physical_code,
          ]),
        ],
      ];
      return `
        <div class="${index ? 'mt-4 pt-4 border-t border-gray-200' : ''}">
          <p class="text-sm font-medium text-gray-700 mb-2">Party ${index + 1}</p>
          ${rowsHtml(rows)}
        </div>
      `;
    })
    .join('');
};

const formatMarital = (detail) => {
  const parties = ['party1', 'party2', 'party3', 'party4', 'party5']
    .map((key) => detail?.[key])
    .filter(Boolean);

  if (!parties.length) return rowsHtml([]);

  return parties
    .map((party, index) => {
      const rows = [
        ['Name', personName(party)],
        ['Marital status', party.long_text || party.marriage_uuid],
      ];
      return `
        <div class="${index ? 'mt-4 pt-4 border-t border-gray-200' : ''}">
          ${rowsHtml(rows)}
        </div>
      `;
    })
    .join('');
};

const formatChildren = (detail) => {
  const blocks = [
    listBlock(
      'Joint children',
      (detail?.present || []).map((c) => childName(c))
    ),
    listBlock(
      'Party 1 children',
      (detail?.party1 || []).map((c) => childName(c))
    ),
    listBlock(
      'Party 2 children',
      (detail?.party2 || []).map((c) => childName(c))
    ),
    listBlock(
      'Other children',
      (detail?.other || []).map((c) => childName(c))
    ),
  ].filter(Boolean);

  if (!blocks.length) {
    return rowsHtml([['Children', detail?.children_yn === false ? 'No children' : null]]);
  }

  return `
    ${rowsHtml([['Has children', detail?.children_yn]])}
    <div class="mt-3">${blocks.join('')}</div>
  `;
};

const formatDevise = (detail) => {
  const wishes = (detail?.wishes || [])
    .map((w) => w.wish || w.instruction)
    .filter(Boolean);
  const bequests = (detail?.bequests || [])
    .map((b) => b.instruction || b.instructions)
    .filter(Boolean);

  return `
    ${listBlock('Wishes', wishes) || rowsHtml([['Wishes', null]])}
    ${listBlock('Special bequests', bequests) || rowsHtml([['Special bequests', null]])}
  `;
};

const formatHeirs = (detail) => {
  const heirNames = [
    ...(detail?.heir_list?.heirs || []),
    ...(detail?.heir_list?.children || []),
    ...(detail?.heir_list?.company || []),
  ]
    .map((h) => h.full_name || h.name || [h.f_name, h.s_name].filter(Boolean).join(' '))
    .filter(Boolean);

  const obliterations = [
    ...(detail?.obliteration_list?.heirs || []),
    ...(detail?.obliteration_list?.company || []),
  ]
    .map((h) => h.full_name || h.name || [h.f_name, h.s_name].filter(Boolean).join(' '))
    .filter(Boolean);

  const notes = (detail?.heir_notes || []).map((n) => n.note).filter(Boolean);

  return `
    ${rowsHtml([
      ['Party 1 sole heir', detail?.party1?.heir_name],
      ['Party 2 sole heir', detail?.party2?.heir_name],
    ])}
    <div class="mt-3">
      ${listBlock('Heirs', heirNames)}
      ${listBlock('Family obliteration', obliterations)}
      ${listBlock('Heir notes', notes)}
    </div>
  `;
};

const formatTrust = (detail) => {
  const trust = detail?.trust || {};
  const trustees = (detail?.trustees || []).map(
    (t) =>
      [t.fname || t.f_name, t.sname || t.s_name].filter(Boolean).join(' ') ||
      t.notes ||
      'Trustee'
  );
  const coexecutors = (detail?.coexecutors || []).map(
    (c) =>
      [c.fname || c.f_name, c.sname || c.s_name].filter(Boolean).join(' ') ||
      c.notes ||
      'Co-executor'
  );

  return `
    ${rowsHtml([
      ['Inheritance in trust', trust.inheritance_in_trust_yn],
      ['Inheritance age', trust.inheritance_age],
      ['Assets to guardian', trust.assets_to_guardian_yn],
      ['Assets to trust', trust.assets_to_trust_yn],
      ['Notes', trust.notes],
    ])}
    <div class="mt-3">
      ${listBlock('Trustees', trustees)}
      ${listBlock('Co-executors', coexecutors)}
    </div>
  `;
};

const formatGuardian = (detail) => {
  const guardians = (detail?.guardian_list || []).map((g) => {
    const name = [g.f_name, g.s_name].filter(Boolean).join(' ') || 'Guardian';
    const rel = [g.rel_party1, g.rel_party2].filter(Boolean).join(' / ');
    return rel ? `${name} (${rel})` : name;
  });

  return `
    ${rowsHtml([['Guardian required', detail?.guardian_yn]])}
    <div class="mt-3">${listBlock('Guardians', guardians)}</div>
  `;
};

const formatEquity = (detail) => {
  const rows = Array.isArray(detail) ? detail : [];
  if (!rows.length) return rowsHtml([]);

  return rows
    .map((item, index) => {
      return `
        <div class="${index ? 'mt-4 pt-4 border-t border-gray-200' : ''}">
          ${rowsHtml([
            ['Category', item.estate_text],
            ['Description', item.estate_desc],
            ['Asset value', item.ass_val],
            ['Liability value', item.liab_val],
            ['Share value', item.share_val],
          ])}
        </div>
      `;
    })
    .join('');
};

const formatSectionDetail = (key, detail) => {
  switch (key) {
    case 'ADs_PersonalDetails':
      return formatPersonal(detail);
    case 'ADs_MaritalStatus':
      return formatMarital(detail);
    case 'ADs_ChildrenDetails':
      return formatChildren(detail);
    case 'ADs_Devise':
      return formatDevise(detail);
    case 'ADs_Heirs':
      return formatHeirs(detail);
    case 'ADs_Trust':
      return formatTrust(detail);
    case 'ADs_Guardian':
      return formatGuardian(detail);
    case 'ADs_Equity':
      return formatEquity(detail);
    default:
      return rowsHtml([]);
  }
};

export default {
  application_uuid: null,
  sections: [],
  async prepare() {
    this.application_uuid = this.vars?.application_uuid || thsPage?.vars?.application_uuid;
    const vars = { application_uuid: this.application_uuid };
    this.recipe.methods.push(await O.mPOST('application/get-application-detail', vars));
    this.recipe.methods.push(await O.mPOST('communal/client-get-comms', {}));
  },
  async render() {
    const rec = this.recipe.methods;
    let data = {};
    if (rec.length) {
      data = await O.PREP(rec);
    }
    await this.loadPage(data);
  },
  async loadPage(data) {
    this.applicationDetail = data['application/get-application-detail']?.data || null;
    const vars = { application_uuid: this.application_uuid };
    const res = await O.PREP([
      await O.mPOST('application/get-application-sections-summary', vars),
    ]);
    const summary = res['application/get-application-sections-summary']?.data;
    this.sections = Array.isArray(summary?.sections) ? summary.sections : [];
    this.renderSections();
    this.renderSubmitForm();
    const comms = data['communal/client-get-comms']?.data;
    await this.renderComms(Array.isArray(comms) ? comms : []);
  },
  async renderComms(rows) {
    const container = U.id('advisorReviewComms');
    if (!container) return;
    container.innerHTML = '';
    if (!rows.length) return;

    const table = document.createElement('table');
    table.className = 'w-full border-collapse text-sm bg-white';

    const head = document.createElement('thead');
    const headRow = document.createElement('tr');
    ['Sent', 'Status', 'Sent to', 'Email', 'Subject', 'Message'].forEach((label) => {
      const cell = document.createElement('th');
      cell.className = 'text-left font-medium text-gray-700 border border-gray-200 px-3 py-2';
      cell.textContent = label;
      headRow.appendChild(cell);
    });
    head.appendChild(headRow);
    table.appendChild(head);

    const body = document.createElement('tbody');
    for (const row of rows) {
      const tr = document.createElement('tr');
      const name = [row.f_name, row.s_name].filter(Boolean).join(' ');
      const sent = row.sent_date ? await U.formatDateTime(row.sent_date) : '—';
      const values = [
        sent,
        row.status || '—',
        name || '—',
        row.email_address || '—',
        row.subject || '—',
      ];
      values.forEach((value) => {
        const cell = document.createElement('td');
        cell.className = 'align-top border border-gray-200 px-3 py-2 text-gray-800';
        cell.textContent = value;
        tr.appendChild(cell);
      });

      const messageCell = document.createElement('td');
      messageCell.className = 'align-top border border-gray-200 px-3 py-2 text-gray-800';
      const bodyHtml = row.body != null && String(row.body).trim() !== '' ? String(row.body) : '';
      if (!bodyHtml) {
        messageCell.textContent = '—';
      } else {
        const viewBtn = document.createElement('button');
        viewBtn.type = 'button';
        viewBtn.className = 'btn-outline inline-flex items-center justify-center p-1';
        viewBtn.title = 'View message';
        viewBtn.setAttribute('aria-label', 'View message');
        viewBtn.appendChild(svg.eye());
        viewBtn.addEventListener('click', (event) => {
          event.preventDefault();
          this.showMessage(row.subject || 'Message', bodyHtml);
        });
        messageCell.appendChild(viewBtn);
      }
      tr.appendChild(messageCell);
      body.appendChild(tr);
    }
    table.appendChild(body);
    container.appendChild(table);
  },
  showMessage(titleText, html) {
    const existing = document.getElementById('advisorReviewMessage');
    if (existing) existing.remove();

    const backdrop = document.createElement('div');
    backdrop.id = 'advisorReviewMessage';
    backdrop.className =
      'fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');

    const close = () => {
      document.removeEventListener('keydown', onKey);
      U.closeModal('advisorReviewMessage');
    };
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    backdrop.addEventListener('click', (event) => {
      if (event.target === backdrop) close();
    });

    const panel = document.createElement('div');
    panel.className =
      'relative w-full max-w-3xl rounded-lg bg-white shadow-xl flex flex-col max-h-[90vh] overflow-hidden';
    panel.addEventListener('click', (event) => event.stopPropagation());

    const header = document.createElement('div');
    header.className = 'flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3';

    const title = document.createElement('h4');
    title.className = 'text-base font-semibold m-0';
    title.textContent = titleText || 'Message';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'btn-outline shrink-0';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', close);

    header.appendChild(title);
    header.appendChild(closeBtn);

    const content = document.createElement('div');
    content.className = 'overflow-y-auto px-4 py-4 text-sm';
    content.innerHTML = html;

    panel.appendChild(header);
    panel.appendChild(content);
    backdrop.appendChild(panel);
    document.body.prepend(backdrop);
  },
  allDetailSectionsComplete() {
    const pages = this.applicationDetail?.pages;
    if (!pages) return false;
    return SECTION_ORDER.every((key) => pages[key]?.complete === true);
  },
  renderSubmitForm() {
    const container = U.id('advisorReviewSubmit');
    if (!container) return;

    const ready = this.allDetailSectionsComplete();
    const title = ready
      ? 'Submit the details to your advisor for review'
      : 'Require further guidance from advisor';
    const intro = ready
      ? 'If you have finished completing the details you can submit them to your advisor for review. Add any comments you want them to read below.'
      : '';
    const placeholder = ready
      ? 'Comments / note for the advisor'
      : 'Questions for your advisor';
    const buttonLabel = ready
      ? 'Submit the Will details to your advisor for review'
      : 'Submit email to your advisor';

    container.innerHTML = `
      <section class="border border-gray-200 rounded-lg p-4 bg-white">
        <h5 class="text-base font-semibold text-gray-800 mb-2">${title}</h5>
        ${intro ? `<p class="text-sm text-gray-600 mb-4">${intro}</p>` : ''}
        <div class="input-group col-span-full mb-4">
          <textarea
            id="advisorReviewNote"
            name="advisorReviewNote"
            rows="4"
            placeholder="${placeholder}"
          ></textarea>
        </div>
        <button type="button" class="btn" onclick="pgList.ADs_submit_advisor_review.submitAdvisorEmail()">
          ${buttonLabel}
        </button>
      </section>
    `;
  },
  async submitAdvisorEmail() {
    const ready = this.allDetailSectionsComplete();
    const endpoint = ready
      ? 'application/client-submit-advisor-review'
      : 'application/client-request-advisor-guidance';
    const request_details = (U.id('advisorReviewNote')?.value || '').trim();
    const vars = {
      application_uuid: this.application_uuid,
      request_details,
    };

    try {
      const res = await O.PREP([await O.mPOST(endpoint, vars)]);
      const result = res[endpoint];
      if (result && result.status !== 'FAILED') {
        await comp.toast(
          ready
            ? 'Your Will details have been submitted to your advisor'
            : 'Your questions have been sent to your advisor'
        );
        await O.changeSubTab('ADs_submit_advisor_review');
      }
    } catch (error) {
      // O.PREP surfaces errors via shared handling
    }
  },
  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  },
  sectionComplete(section) {
    if (section && Object.prototype.hasOwnProperty.call(section, 'complete')) {
      return section.complete;
    }
    const pages = this.applicationDetail?.pages;
    if (!pages) return true;
    const list = Array.isArray(pages) ? pages : Object.values(pages);
    const page = list.find((item) => item?.html_text === section.key || item?.key === section.key);
    if (page && Object.prototype.hasOwnProperty.call(page, 'complete')) {
      return page.complete;
    }
    return true;
  },
  renderSections() {
    const container = U.id('advisorReviewSections');
    if (!container) return;
    container.innerHTML = '';

    const byKey = {};
    this.sections.forEach((section) => {
      byKey[section.key] = section;
    });

    const sections = SECTION_ORDER.map((key) => byKey[key] || { key, label: key, detail: null });
    const status = U.id('advisorReviewStatus');
    if (status) {
      const anyIncomplete = sections.some((section) => this.sectionComplete(section) === false);
      if (anyIncomplete) {
        status.textContent = 'Some sections still need input';
        status.classList.remove('hide');
      } else {
        status.textContent = '';
        status.classList.add('hide');
      }
    }

    sections.forEach((section, index) => {
      const complete = this.sectionComplete(section);
      const pending = complete === false ? ' (Still to be completed)' : '';
      const numberClass = complete === true ? 'bg-success' : 'bg-primary';
      const sectionEl = U.createHTMLElementFromString(`
        <section class="border border-gray-200 rounded-lg p-4 bg-white">
          <div class="flex items-center gap-3 mb-3">
            <span class="inline-flex items-center justify-center w-7 h-7 rounded-full ${numberClass} text-white text-sm font-medium">${index + 1}</span>
            <h5 class="text-base font-semibold text-gray-800 mb-0">${this.escapeHtml(section.label)}${pending}</h5>
          </div>
          <div>${formatSectionDetail(section.key, section.detail)}</div>
        </section>
      `);
      container.appendChild(sectionEl);
    });
  },
  async savePage(nextPrev) {
    if (nextPrev && thsPage?.changeSideNavTab) {
      await thsPage.changeSideNavTab(nextPrev);
    }
  },
};
